// src/features/manual/components/ManualPage.tsx

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocaleState, useTranslate } from 'react-admin';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { Box, Button, ButtonGroup, Skeleton } from '@mui/material';
import { MenuBookRounded as ManualIcon } from '@mui/icons-material';
import { SectionHeader } from '@/components/common/SectionHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import { getCurrentRole } from '@/utils/auth';
import { useManualChapter, useManualIndex } from '../hooks/useManual';
import { MANUAL_LANGS, type ManualLang } from '../types';
import { headingAnchors, splitChapter } from '../utils/chapterBlocks';
import { isManualStaff, manualLangFromLocale, sectionDomId } from '../utils/labels';
import { ManualRail } from './ManualRail';
import { MarkdownBody } from './MarkdownBody';
import { StaffBlock } from './StaffBlock';

const jumpTo = (anchor: string) => {
  const target = document.getElementById(sectionDomId(anchor));
  if (!target) return;
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  target.focus({ preventScroll: true });
};

/** «dd/mm/aaaa» / «mm/dd/yyyy» of a `YYYY-MM-DD` date, without a time-zone shift. */
const formatUpdated = (value: string, lang: ManualLang): string => {
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return value;
  return new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : 'es-ES', { dateStyle: 'long', timeZone: 'UTC' }).format(
    new Date(Date.UTC(y, m - 1, d))
  );
};

/**
 * «Manual de uso»: the index of parts and chapters beside the open chapter. The language follows the
 * interface and can be switched here for the page. The API sends each reader their edition: Roommatik
 * staff receive staff chapters and «Solo personal de Roommatik» notes; customers never do. The page
 * draws staff notes only for staff, as a second guard.
 */
export const ManualPage: React.FC = () => {
  const translate = useTranslate();
  const { chapterId } = useParams<{ chapterId: string }>();
  const location = useLocation();
  const [uiLocale] = useLocaleState();
  const [lang, setLang] = useState<ManualLang>(() => manualLangFromLocale(uiLocale));
  const articleRef = useRef<HTMLElement | null>(null);
  const staff = isManualStaff(getCurrentRole());

  useEffect(() => setLang(manualLangFromLocale(uiLocale)), [uiLocale]);

  const index = useManualIndex(lang);
  const chapter = useManualChapter(chapterId, lang);

  // A link into the manual («Ver en el manual», a chapter link) names the section to open.
  const linkState = location.state as { anchor?: string | null } | null;
  useEffect(() => {
    if (!chapter.data) return;
    const anchor = linkState?.anchor;
    window.setTimeout(() => (anchor ? jumpTo(anchor) : articleRef.current?.scrollIntoView({ block: 'start' })), 0);
    // `location.key` marks a new navigation; the state object itself is not stable between renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, chapter.data]);

  const blocks = useMemo(
    () => (chapter.data ? splitChapter(chapter.data.body, { includeStaff: staff }) : []),
    [chapter.data, staff]
  );
  const anchors = useMemo(() => headingAnchors(chapter.data?.sections ?? []), [chapter.data]);

  const firstChapterId = index.data?.parts.flatMap((part) => part.chapters)[0]?.id;
  if (!chapterId && firstChapterId) {
    return <Navigate to={`/manual/${encodeURIComponent(firstChapterId)}`} replace />;
  }

  const chapterData = chapter.data;
  const subtitle = [
    translate('manual.title'),
    chapterData?.updated ? translate('manual.meta.updated', { date: formatUpdated(chapterData.updated, lang) }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const languageSwitch = (
    <ButtonGroup size="small" aria-label={translate('manual.lang.label')}>
      {MANUAL_LANGS.map((code) => (
        <Button
          key={code}
          variant={lang === code ? 'contained' : 'outlined'}
          aria-pressed={lang === code}
          onClick={() => setLang(code)}
          sx={{ minWidth: 44, minHeight: 32 }}
        >
          {translate(`manual.lang.${code}`)}
        </Button>
      ))}
    </ButtonGroup>
  );

  const renderContent = () => {
    if (index.error && !index.data) return <FetchErrorBanner onRetry={index.refetch} />;
    if (index.data && !firstChapterId) {
      return <EmptyState icon={<ManualIcon />} message={translate('manual.state.empty')} />;
    }
    if (chapter.error) {
      return chapter.status === 404 ? (
        <EmptyState icon={<ManualIcon />} message={translate('manual.state.notFound')} />
      ) : (
        <FetchErrorBanner onRetry={chapter.refetch} />
      );
    }
    if (!chapterData) {
      return (
        <Box aria-busy>
          <Skeleton variant="text" width="60%" height={28} />
          <Skeleton variant="rectangular" height={220} sx={{ mt: 2, borderRadius: 1 }} />
        </Box>
      );
    }
    return blocks.map((block, position) =>
      block.kind === 'staff' ? (
        <StaffBlock key={position} markdown={block.markdown} anchors={anchors} />
      ) : (
        <MarkdownBody key={position} markdown={block.markdown} anchors={anchors} />
      )
    );
  };

  return (
    <Box>
      <SectionHeader
        title={chapterData?.title ?? translate('manual.title')}
        subtitle={chapterData ? subtitle : undefined}
        rightActions={languageSwitch}
        sticky={false}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: '260px minmax(0, 1fr)' },
          columnGap: 4,
          alignItems: 'start',
        }}
      >
        {index.data ? (
          <ManualRail
            index={index.data}
            activeChapterId={chapterId}
            sections={chapterData?.sections ?? []}
            onJump={jumpTo}
            lang={lang}
          />
        ) : (
          <Box />
        )}
        <Box
          component="article"
          ref={articleRef}
          sx={(theme) => ({
            minWidth: 0,
            p: { xs: 2, sm: 3 },
            mb: 4,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.custom.radius.card,
          })}
        >
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default ManualPage;
